import { Inject, Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { AppConfigService } from '../../app-config.service';
import { AccessService, Feature, StubAccessService } from '../access';
import { PermissionsApiService, ResourceScope } from '@vendasta/platform-users';
import { Observable, catchError, firstValueFrom, map, of } from 'rxjs';

export interface PlatformPermissions {
  crmContacts?: boolean;
  crmCompanies?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DefaultPageGuard implements CanActivate {
  private readonly permissions: Map<string, PlatformPermissions> = new Map();

  constructor(
    private readonly configService: AppConfigService,
    private readonly router: Router,
    @Inject(StubAccessService) private accessService: AccessService,
    private readonly platformPermissionsService: PermissionsApiService,
  ) {}

  async canActivate(): Promise<boolean | UrlTree> {
    const config = this.configService.config;

    if (config.userCanAccessDashboard) {
      return true;
    }
    if (config.userCanAccessAccounts) {
      return this.router.createUrlTree(['/manage-accounts']);
    }
    if (config.canEnableApps) {
      return this.router.createUrlTree(['/marketplace/products']);
    }
    if (config.userCanAccessMarketing) {
      return this.router.createUrlTree(['/campaigns/acquire']);
    }
    if (config.userCanAccessSales) {
      return this.router.createUrlTree(['/st/sales-overview']);
    }
    if (config.userCanAccessBilling) {
      return this.router.createUrlTree(['/billing/reports']);
    }
    if (config.userCanCustomizeWhitelabel) {
      return this.router.createUrlTree(['/customize-design']);
    }
    if (config.unifiedUserId) {
      const permissions = await this.getPermissions(config.partnerId, config.unifiedUserId);
      if (permissions.crmCompanies) {
        return this.router.createUrlTree(['/crm/company']);
      }
      if (permissions.crmContacts) {
        return this.router.createUrlTree(['/crm/contact']);
      }
    }
    if (this.accessService?.hasAccessToFeature(Feature.pipeline)) {
      return this.router.createUrlTree(['/pipeline/board']);
    }

    return this.router.createUrlTree(['/access-denied']);
  }

  private async getPermissions(partnerId: string, userId: string): Promise<PlatformPermissions> {
    const key = `${partnerId}:${userId}`;
    if (this.permissions.has(key)) {
      return this.permissions.get(key);
    }
    const permissions = await firstValueFrom(this.fetchPlatformPermissions(partnerId, userId));
    this.permissions.set(key, permissions);
    return permissions;
  }

  private fetchPlatformPermissions(partnerId: string, userId: string): Observable<PlatformPermissions> {
    return this.platformPermissionsService.getPermissions({ partnerId, userId }).pipe(
      map((resp) => {
        const crmContacts = resp?.permissions?.contacts?.writeScope === ResourceScope.RESOURCE_SCOPE_ALL;
        const crmCompanies = resp?.permissions?.companies?.writeScope === ResourceScope.RESOURCE_SCOPE_ALL;
        return { crmContacts, crmCompanies } as PlatformPermissions;
      }),
      catchError(() => of({} as PlatformPermissions)),
    );
  }
}
